import React, { FC, useRef, useState } from 'react';
import { message, Progress, Space, Spin } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { api, endpoints } from '../../../../../../../api';
import { CustomButton } from '../../../../../../../styles/styled';

interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

interface UploadPart {
  data: Blob;
  index: number;
  eof?: boolean;
}

const CHUNK_SIZE = 25000000;

type UploadBigFileComponentProps = {
  formats?: string[];
  setBook: (file_id: number, title: string) => void;
  title?: string;
};

export const UploadBigFileComponent: FC<UploadBigFileComponentProps> = ({ formats, setBook, title }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<UploadProgress>({
    loaded: 0,
    total: 0,
    percent: 0,
  });
  const [fileLoading, setFileLoading] = useState<'neutral' | 'loading' | 'loaded'>('neutral');

  async function uploadFile(
    fileSize: number,
    file: Blob,
    onProgress: (progress: UploadProgress) => void,
    hash: string,
    ext: string,
    title,
    idx: number,
    amount: number,
    eof?: boolean,
  ) {
    try {
      return await new Promise<void>((resolve, reject) => {
        const headers = {
          portion: idx,
          'file-hash': hash,
          'file-ext': ext.split('/')[1],
          'file-title': 'Book',
          'end-upload': !!eof,
        };

        const config = {
          headers: headers,
        };

        api
          .post(endpoints.postBigFile(), eof ? '' : file, config)
          .then((el) => {
            resolve();

            if (amount === idx + 1) setFileLoading('loaded');

            const progress: UploadProgress = {
              loaded: file.size,
              total: fileSize,
              percent: (file.size / fileSize) * 100,
            };
            onProgress(progress);

            if (eof) {
              setBook(el.data.file_id, title);
              message.success('Файл загружен');
            }
          })
          .finally(() => resolve())
          .catch((error) => reject(error));
      });
    } catch (err) {}
  }

  async function uploadFileByParts(file: File) {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const parts: UploadPart[] = [];

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      parts.push({ data: chunk, index: i + 1 });

      if (i === totalChunks - 1) parts.push({ data: chunk, index: i, eof: true });
    }

    const hash = await uuidv4();

    function recursivePromiseFunc(parts: UploadPart[], idx: number) {
      if (idx < parts.length) {
        uploadFile(
          file.size,
          parts[idx].data,
          (progress) => {
            setProgress((prevState) => {
              return {
                ...progress,
                loaded: progress.loaded + prevState.loaded,
                percent: ((progress.loaded + prevState.loaded) / file.size) * 100,
              };
            });
          },
          hash,
          file.type,
          file.name,
          idx,
          parts.length,
          parts[idx]?.eof,
        ).then(() => {
          recursivePromiseFunc(parts, idx + 1);
        });
      }
    }

    try {
      recursivePromiseFunc(parts, 0);
    } catch (error) {}
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setProgress({ loaded: 0, percent: 0, total: 0 });

    if (selectedFile) {
      uploadFileByParts(selectedFile).then();
      setFileLoading('loading');
    }
  };

  return (
    <Space align="center">
      <input
        ref={inputRef}
        accept={formats?.join(',')}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <CustomButton
        type="button"
        width="800px"
        lined
        disabled={fileLoading === 'loading'}
        disable={fileLoading === 'loading'}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        <Space direction="horizontal" size={10}>
          <>{title ? title : fileLoading === 'loading' ? 'Идет загрузка файла' : 'Выберите файл'}</>
          {fileLoading === 'loading' && <Spin size="small" />}
        </Space>
      </CustomButton>
      <Space align="center" direction="horizontal" size="small">
        {fileLoading === 'loading' && (
          <Progress width={35} type="circle" percent={parseInt(progress.percent.toFixed(2))} />
        )}
      </Space>
    </Space>
  );
};
